import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import styles from './styles/dashboardStyles';
import { RouteComponentProps, useHistory, useRouteMatch } from 'react-router-dom';
import routes from '../routes';
import { getIdFromAppId, getIdFromOrgId } from '../helpers';
import NewPasswordComponent from './NewPasswordComponent';
import ErrorBoundary from './ErrorBoundary';
import qs from 'query-string';
import * as Analytics from '../utils/analytics';
import { AppRouteComponentMatchParams } from '../typescriptHelpers';
import * as GoogleAnalytics from '../utils/ga';
import { selectApp, selectOrg, selectOrgHasAnyApps, selectUser } from '../selectors/selectors';
import { IsMobileWrapper, useIsMobile } from '../mediaQueriesHelpers';
import { DashboardRoutesProps, DashboardRoutes } from './DashboardRoutes';
import { AppComponentWrapper } from './HOCs/AppComponentWrapper';
import { NavigationSidebar } from './sidebar/NavigationSidebar';
import OrganizationNavigator from './OrganizationNavigator';
import Layout from 'antd/lib/layout/layout';
import { useNavigationContext } from '../utils/hooks/useNavigationContext';

const NavigationSidebarWrapper: React.FC = () => {
    const isMobile = useIsMobile();
    const { orgId, appId } = useNavigationContext();

    if (isMobile) {
        return <OrganizationNavigator />;
    }

    return (
        <Layout style={{ minHeight: '100%', maxWidth: '280px' }}>
            <NavigationSidebar orgId={orgId} appId={appId} />
        </Layout>
    );
};

const DashboardRoutesContainer: React.FC = () => {
    const hasApps = useSelector((state: RootState) => selectOrgHasAnyApps(state));

    const match = useRouteMatch<AppRouteComponentMatchParams>(routes.app);

    const orgIdParam = match?.params?.orgIdParam;
    const appIdParam = match?.params?.appIdParam;

    const orgId = useSelector((state: RootState) => {
        return orgIdParam ? getIdFromOrgId(orgIdParam, state) : null;
    });
    const appId = useSelector((state: RootState) => {
        return orgIdParam && appIdParam ? getIdFromAppId(orgIdParam, appIdParam, state) : null;
    });

    const isRestrictedApp = useSelector((state: RootState) => {
        if (orgIdParam && appIdParam) {
            const appId = getIdFromAppId(orgIdParam, appIdParam, state);

            if (appId) {
                const app = selectApp(state, appId);

                return app.isRestrictedApp;
            }
        }

        return false;
    });

    const props: DashboardRoutesProps = {
        orgId: orgId,
        appId: appId,
        hasApps: hasApps,
        isRestrictedApp: isRestrictedApp,
    };

    if (orgId && appId) {
        return (
            <AppComponentWrapper orgId={orgId} appId={appId} showLoader showError>
                <DashboardRoutes {...props} />
            </AppComponentWrapper>
        );
    } else {
        return <DashboardRoutes {...props} />;
    }
};

const Dashboard: React.FC<RouteComponentProps> = () => {
    const history = useHistory();

    const qParams = qs.parse(history.location.search);
    const signUpAuthSource = qParams?.signUpAuthSource;

    const { orgId, userId } = useSelector((state: RootState) => {
        const orgId = selectOrg(state)?.id;
        const userId = selectUser(state).id;

        return {
            orgId,
            userId,
        };
    }, shallowEqual);

    React.useEffect(() => {
        if (signUpAuthSource) {
            const userProps: GoogleAnalytics.GAUserProperties = {
                userId,
                orgId: orgId ?? '',
            };

            Analytics.trackSignupConversionPixels(signUpAuthSource as GoogleAnalytics.SignUpLoginMethod, userProps);

            // clear query params
            history.replace({ search: null });
        }
    }, [signUpAuthSource]);

    return (
        <ErrorBoundary showRedirectLink={true}>
            <IsMobileWrapper>
                {(isMobile) => (
                    <div
                        style={{
                            ...styles.dashboardContainerStyle,
                            ...(isMobile ?
                                {
                                    flexDirection: 'column',
                                    overflowY: 'scroll',
                                }
                            :   {}),
                        }}
                    >
                        <NewPasswordComponent />
                        <NavigationSidebarWrapper />
                        <ErrorBoundary showRedirectLink={false}>
                            <DashboardRoutesContainer />
                        </ErrorBoundary>
                    </div>
                )}
            </IsMobileWrapper>
        </ErrorBoundary>
    );
};

export default Dashboard;
